import { useState } from 'react';
import RouteTitleHandler from '../components/common/RouteTitleHandler';
import { LoaderCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { firstLoginReset, login } from '../services/authService';
import { useNavigate } from 'react-router-dom';
import Modal from '../components/common/Modal';

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [firstLogin, setFirstLogin] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const handleFirstLoginSubmit = async (e) => {
    e.preventDefault();
    if (employeeId.trim() === '' || oldPassword.trim() === '' || newPassword.trim() === '') {
      toast.error('Employee ID, old password and new password are required!', {
        position: 'top-center',
        duration: 4000,
      });
      return;
    }

    setIsLoading(true);

    try {
      await firstLoginReset(employeeId, oldPassword, newPassword);
      toast.success('First login successful!', {
        position: 'top-center',
        duration: 4000,
      });
      navigate('/chat');
    } catch (error) {
      console.error('First login failed:', error);
    } finally {
      setIsLoading(false);
    }
  }

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (email.trim() === '' || password === '') {
      toast.error('Username and password are required!', {
        position: 'top-center',
        duration: 4000,
      });
      return;
    }

    setIsLoading(true);

    try {
      await login(email, password);

      toast.success('Login successful!', {
        position: 'top-center',
        duration: 4000,
      });
      navigate('/chat');
    } catch (error) {
      console.error('Login failed:', error);

      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;

      // Check for first login scenario - handle both with and without period
      if (status === 403 && (
        detail === 'First login detected, please reset your password' ||
        detail === 'First login detected, please reset your password.'
      )) {
        console.log('First login detected, showing modal');
        setFirstLogin(true);
        return;
      }
      
      // Handle different types of errors
      let errorMessage = 'Login failed. Please try again.';
      
      if (error?.response?.status === 401) {
        errorMessage = 'Invalid username or password.';
      } else if (error?.response?.status === 403) {
        errorMessage = 'Account access forbidden. Please contact support.';
      } else if (error?.response?.status === 429) {
        errorMessage = 'Too many login attempts. Please try again later.';
      } else if (error?.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      // } else if (error?.code === 'NETWORK_ERROR' || error?.message?.includes('Network Error')) {
      //   errorMessage = 'Network connection failed. Please check your internet connection.';
      } else if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
        errorMessage = 'Login request timed out. Please try again.';
      } else if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error?.message && error.message !== 'Request failed with status code 401') {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, {
        position: 'top-center',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <RouteTitleHandler title='ACG Chat - Login' />
      <main className="min-h-screen min-w-screen">
        <div className="absolute h-full w-full bg-gradient-to-r from-[#090F16] via-[#081830] to-[#08121C]">
          <div className="absolute h-[350px] w-[51px] bg-[#FA0041]/60 rounded-full -rotate-45 top-[30%] left-[60%] translate-x-[-50%] translate-y-[-50%]"></div>
          <div className="absolute h-[350px] w-[51px] bg-[#FA0041]/60 rounded-full -rotate-45 top-[40%] left-[40%] translate-x-[-40%] translate-y-[-40%]"></div>
          <div className="absolute h-[350px] w-[51px] bg-[#FA0041]/60 rounded-full -rotate-45 top-[40%] left-[50%] translate-x-[-60%] translate-y-[-60%]"></div>
          <div className="absolute h-[350px] w-[51px] bg-[#4285F4]/40 rounded-full -rotate-45 top-[25%] left-[50%] translate-x-[-50%] translate-y-[-50%]"></div>
          <div className="absolute h-[350px] w-[51px] bg-[#4285F4]/40 rounded-full -rotate-45 top-[25%] left-[38%] translate-x-[-40%] translate-y-[-40%]"></div>
          <div className="absolute h-[350px] w-[51px] bg-[#4285F4]/40 rounded-full -rotate-45 top-[45%] left-[30%] translate-x-[-60%] translate-y-[-60%]"></div>
        </div>
        <div className="absolute h-full w-full bg-[#1E1E1E]/10 backdrop-blur-2xl"></div>
        <div className="absolute p-2 bg-black/50 shadow-md border border-black/80 backdrop-blur-lg text-white text-center top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] min-w-[420px] px-6 rounded-lg min-h-1/3">
          <div className="mb-4 mt-4">
            <img src='/acg-logo.png' className="h-9 w-auto mx-auto" alt="acg-logo" />
            <p className="uppercase text-xl font-bold">Smart assistant</p>
          </div>
          <div className="mb-6">
            <h1 className="text-2xl">Log in</h1>
            <p>Enter email and password to login</p>
          </div>
          <form onSubmit={handleLoginSubmit} className="space-y-4 mb-14">
            <div className="flex flex-col text-left">
              <label htmlFor="email">Username</label>
              <input
                id="email"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                className="border border-gray-700 p-2 rounded-md bg-black/50 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter Username"
              />
            </div>
            <div className="flex flex-col text-left">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isLoading && email.trim() && password.trim()) {
                    e.preventDefault();
                    handleLoginSubmit(e);
                  }
                }}
                disabled={isLoading}
                className="border border-gray-700 p-2 rounded-md bg-black/50 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="**********"
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="text-center w-full py-2 bg-[#FA0041] rounded-md hover:bg-[#FA0041]/80 duration-300 disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <LoaderCircle className="animate-spin h-4 w-4" />
                  <span>Logging in...</span>
                </>
              ) : (
                'Log in'
              )}
            </button>
          </form>
        </div>
        {firstLogin && (
          <Modal open={firstLogin} setOpen={(open) => {
            if (!open) {
              setFirstLogin(false);
            }
          }} modalWidth="500px" isDark={true}>
            <div className="p-6 z-50 bg-black/50 border border-gray-700 rounded-lg text-white space-y-4 flex flex-col items-center">
              <img src="/acg-logo.png" alt="acg-logo" className="h-10 w-auto" />
              <h1 className="text-2xl">First Login</h1>
              <p className='text-sm text-gray-400'>Please enter your employee ID, old password and new password to reset your password.</p>
              <div className="flex flex-col gap-2 w-full">
                <label htmlFor="employee_id">Employee ID</label>
                <input
                  id="employee_id"
                  className="border border-gray-700 p-2 rounded-md bg-black/50 disabled:opacity-50 disabled:cursor-not-allowed text-white placeholder:text-white/50"
                  type="text"
                  placeholder="Enter Employee ID"
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-2 w-full">
                <label htmlFor="old_password">Old Password</label>
                <input
                  id="old_password"
                  className="border border-gray-700 p-2 rounded-md bg-black/50 disabled:opacity-50 disabled:cursor-not-allowed text-white placeholder:text-white/50"
                  type="password"
                  placeholder="Enter Old Password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-2 w-full">
                <label htmlFor="new_password">New Password</label>
                <input
                  id="new_password"
                  className="border border-gray-700 p-2 rounded-md bg-black/50 disabled:opacity-50 disabled:cursor-not-allowed text-white placeholder:text-white/50"
                  type="password"
                  placeholder="Enter New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              <button className="bg-[#FA0041] text-white px-4 py-2 rounded-md w-full" onClick={handleFirstLoginSubmit} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <LoaderCircle className="animate-spin h-4 w-4" />
                    <span>Resetting password...</span>
                  </>
                ) : (
                  'Reset Password'
                )}
              </button>
            </div>
          </Modal>
        )}
      </main>
    </>
  );
}